import { TaskQueue, TaskId } from "./taskQueueApi";
import api from "./api";

export type RequestChatResponse = {
  response_message: any;
  chat_id: string;
};

export class ChatRequestTaskQue extends TaskQueue<RequestChatResponse> {
  mapChatIdToTaskId: Record<string, TaskId> = {};

  constructor(token: string, pollInterval?: number) {
    super(token, pollInterval, "Chat response request was cancelled.");
  }

  private getChatKey(chatId?: number): string {
    return chatId !== undefined ? `${chatId}` : "__default__";
  }

  chatIsGenerating(chatId: number | undefined): boolean {
    const key = this.getChatKey(chatId);
    return this.mapChatIdToTaskId[key] !== undefined;
  }

  async cancel(chatId: number): Promise<unknown> {
    const taskId = this.getTaskIdForChat(chatId);
    if (!taskId) {
      console.warn("Task id not found");
      return;
    }
    try {
      await super.cancelTask(taskId);
      const key = this.getChatKey(chatId);
      delete this.mapChatIdToTaskId[key];
    } catch {
      console.warn("Error cancelling task");
    }
    return;
  }
  private getTaskIdForChat(chatId: number | undefined): string | undefined {
    const key = this.getChatKey(chatId);
    return this.mapChatIdToTaskId[key];
  }

  async requestResponse(chatId: number): Promise<RequestChatResponse> {
    if (this.chatIsGenerating(chatId)) {
      console.warn(
        "Chat is already generating a response, Please wait for it to finish"
      );
      return;
    }

    const chatKey = this.getChatKey(chatId);
    const response = await api.post<{ task_id: TaskId }>(
      `/api/_internal/gpt/chat/request_response`,
      { chat_id: chatId },
      {
        headers: { token: this.token },
      }
    );

    const { task_id: taskId } = response.data;
    this.mapChatIdToTaskId[chatKey] = taskId;
    this.addTaskId(taskId, "Chat Response");

    try {
      const rawResult = await super.waitUntilTaskIsDone(taskId);
      const taskResult = (rawResult as unknown) as { messages: any[] };

      const responseMessage = taskResult.messages?.[0] || null;

      return {
        response_message: responseMessage,
        chat_id: String(chatId),
      };
    } finally {
      delete this.mapChatIdToTaskId[chatKey];
      if (this.taskIdExists(taskId)) {
        this.removeTaskId(taskId);
      }
    }
  }
}
